import { type EnvironmentViewMode } from "@/components/ai/ViewModeToggle";
import { useEffect, useMemo } from "react";
import { canOpen } from "@/routes/environments/phase";
import type { PlainEnvironment } from "@/queries/environment-queries";
import { useEnvironmentSettings } from "./use-environment-settings";
import { EnvironmentPhase } from "gitpod-next-api/gitpod/v1/environment_pb";
import useIsMobileViewport from "@/hooks/use-is-mobile-viewport";
import { useFlagIsMobileVscodeEnabled } from "@/hooks/use-flag";

/**
 * Hook for managing view mode (details, changes, vscode) per environment
 * Persists the user's preferred view mode but enforces business rules
 * Automatically defaults to "changes" when environment is running, falls back to "details" when environment can't be opened
 */
export function useEnvironmentViewMode(
    environment?: PlainEnvironment,
): [EnvironmentViewMode, (mode: EnvironmentViewMode) => void] {
    const { isMobileViewport: isMobile } = useIsMobileViewport();
    const { value: isMobileVscodeEnabled } = useFlagIsMobileVscodeEnabled();

    // Mobile should block VSCode only when the feature flag is disabled
    const shouldBlockVscodeOnMobile = isMobile && !isMobileVscodeEnabled;

    // Determine smart default based on environment state and VS Code connection
    const smartDefault = useMemo((): EnvironmentViewMode => {
        if (!environment) return "details";

        // Force "details" mode when in VS Code context (embedded webview) or on mobile
        if (window.isWebview || isMobile) {
            return "details";
        }

        const actual = environment.status?.phase ?? EnvironmentPhase.UNSPECIFIED;

        // Default to "changes" when environment is running (agent is working) - desktop only
        if (actual === EnvironmentPhase.RUNNING && canOpen(environment)) {
            return "changes";
        }

        return "details";
    }, [environment, isMobile]);

    const { value: viewMode, setValue: setViewMode } = useEnvironmentSettings(
        environment?.id ?? "__no_env__",
        "viewmode",
        smartDefault,
    );

    // Force "details" if environment can't be opened OR if in VS Code context OR on mobile (when feature flag is disabled)
    useEffect(() => {
        if (
            environment &&
            (!canOpen(environment) || window.isWebview || shouldBlockVscodeOnMobile) &&
            viewMode === "vscode"
        ) {
            setViewMode("details");
        }
    }, [environment, viewMode, setViewMode, shouldBlockVscodeOnMobile]);

    return [viewMode as EnvironmentViewMode, setViewMode as (mode: EnvironmentViewMode) => void];
}
